import { useState, useEffect, useRef } from "react";
import {
  TbX, TbTag, TbShield, TbShieldOff, TbShieldHalf,
  TbToolsKitchen2, TbUsers, TbClock,
  TbPercentage, TbCheck, TbMoodKid,
  TbUserPlus, TbHome, TbDoor, TbLink, TbLinkOff,
  TbReceipt, TbReceiptOff, TbCurrencyEuro,
  TbBed, TbCalendar, TbMoon
} from "react-icons/tb";

const Field = ({ label, hint, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
  </div>
);

const Input = ({ icon: Icon, ...props }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none" />}
    <input {...props} className={`w-full ${Icon ? "pl-9" : "pl-4"} pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-800 outline-none focus:border-[var(--k-green)] focus:bg-white transition-all duration-150 placeholder:text-gray-300`} />
  </div>
);

const NumInput = ({ icon: Icon, suffix, ...props }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none" />}
    <input type="number" {...props} className={`w-full ${Icon ? "pl-9" : "pl-4"} ${suffix ? "pr-12" : "pr-4"} py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-800 outline-none focus:border-[var(--k-green)] focus:bg-white transition-all duration-150`} />
    {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">{suffix}</span>}
  </div>
);

const SectionTitle = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 pt-2">
    <div className="w-6 h-6 rounded-lg bg-[rgb(var(--k-green-rgb)/0.1)] flex items-center justify-center shrink-0">
      <Icon className="text-[var(--k-green)] text-sm" />
    </div>
    <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">{label}</span>
    <div className="flex-1 h-px bg-gray-100" />
  </div>
);

const PolicyCard = ({ opt, selected, onClick }) => (
  <div onClick={onClick} className={`relative flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-150 select-none ${selected ? opt.activeClass : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"}`}>
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${opt.iconBg}`}>
      <opt.Icon className={`text-base ${opt.iconColor}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-800">{opt.label}</p>
      <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
    </div>
    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 mt-1 transition-all ${selected ? "border-[var(--k-green)] bg-[var(--k-green)]" : "border-gray-300"}`}>
      {selected && <TbCheck className="text-white text-[10px] stroke-[3]" />}
    </div>
  </div>
);

// 🆕 PÉRIODES — carte de sélection du type de séjour (même style que PolicyCard)
const StayTypeCard = ({ opt, selected, onClick }) => (
  <div onClick={onClick} className={`relative flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-150 select-none ${selected ? opt.activeClass : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"}`}>
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${opt.iconBg}`}>
      <opt.Icon className={`text-base ${opt.iconColor}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-800">{opt.label}</p>
      <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
    </div>
    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 mt-1 transition-all ${selected ? "border-[var(--k-green)] bg-[var(--k-green)]" : "border-gray-300"}`}>
      {selected && <TbCheck className="text-white text-[10px] stroke-[3]" />}
    </div>
  </div>
);

// 🆕 FRAIS FIXES — carte de sélection du mode de pénalité (% vs €), même style que le toggle taxe
const PenaltyModeCard = ({ label, desc, selected, onClick }) => (
  <div onClick={onClick} className={`flex flex-col gap-0.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all duration-150 select-none ${selected ? "border-[var(--k-green)] bg-[rgb(var(--k-green-rgb)/0.05)]" : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"}`}>
    <div className="flex items-center justify-between">
      <p className={`text-sm font-semibold ${selected ? "text-gray-800" : "text-gray-500"}`}>{label}</p>
      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${selected ? "border-[var(--k-green)] bg-[var(--k-green)]" : "border-gray-300"}`}>
        {selected && <TbCheck className="text-white text-[10px] stroke-[3]" />}
      </div>
    </div>
    <p className="text-[10px] text-gray-400">{desc}</p>
  </div>
);

const MealChip = ({ opt, selected, onClick }) => (
  <button type="button" onClick={onClick} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all duration-150 ${selected ? "border-[var(--k-green)] bg-[rgb(var(--k-green-rgb)/0.05)] text-[var(--k-green)] font-semibold" : "border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200 hover:bg-white"}`}>
    {opt.emoji && <span className="text-base">{opt.emoji}</span>}
    <span>{opt.label}</span>
  </button>
);

const RoomChip = ({ room, checked, hasPrice, loading, onChange }) => (
  <button type="button" onClick={() => onChange(room._id, !checked)} disabled={loading}
    className={`relative flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border text-left transition-all duration-150 select-none w-full ${loading ? "opacity-50 cursor-wait" : "cursor-pointer"} ${checked ? "border-[var(--k-green)] bg-[rgb(var(--k-green-rgb)/0.05)]" : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"}`}>
    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${checked ? "bg-[rgb(var(--k-green-rgb)/0.15)]" : "bg-gray-100"}`}>
      <TbDoor className={`text-sm ${checked ? "text-[var(--k-green)]" : "text-gray-400"}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className={`text-sm font-semibold truncate ${checked ? "text-gray-800" : "text-gray-600"}`}>
        {room.title || `Chambre ${room._id.slice(-4)}`}
      </p>
      {checked && (
        <p className="text-[10px] mt-0.5 flex items-center gap-1">
          {hasPrice
            ? <><TbLink className="text-[var(--k-green)]" /><span className="text-[var(--k-green)]">Prix configuré</span></>
            : <><TbLinkOff className="text-amber-400" /><span className="text-amber-500">Associée — prix à définir</span></>
          }
        </p>
      )}
    </div>
    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${checked ? "border-[var(--k-green)] bg-[var(--k-green)]" : "border-gray-300"}`}>
      {checked && <TbCheck className="text-white text-[10px] stroke-[3]" />}
    </div>
  </button>
);

const POLICY_OPTIONS = [
  { value: "free", label: "Annulation gratuite", desc: "Le client annule sans frais dans les délais", Icon: TbShield, iconBg: "bg-green-50", iconColor: "text-green-500", activeClass: "border-green-200 bg-green-50/60" },
  { value: "partial", label: "Annulation partielle", desc: "Des frais s'appliquent après le délai", Icon: TbShieldHalf, iconBg: "bg-amber-50", iconColor: "text-amber-500", activeClass: "border-amber-200 bg-amber-50/60" },
  { value: "non_refundable", label: "Non remboursable", desc: "Aucun remboursement possible", Icon: TbShieldOff, iconBg: "bg-red-50", iconColor: "text-red-400", activeClass: "border-red-200 bg-red-50/60" }
];

/* 🆕 PÉRIODES — trois unités de vente, et non plus un seuil de nuits libre.

   L'hébergeur ne saisit plus "à partir de combien de nuits" : il choisit
   comment il vend sa chambre. Le nombre de nuits en découle (1, 7 ou 30) et
   est calculé côté serveur, ce qui évite qu'un plan "mensuel" se retrouve
   avec un seuil de 12 nuits et un prix qui ne veut plus rien dire. */
const STAY_TYPE_OPTIONS = [
  { value: "short_stay", label: "À la nuit",    desc: "Prix par nuit, réservable dès 1 nuit",          Icon: TbBed,          iconBg: "bg-blue-50", iconColor: "text-blue-400", activeClass: "border-blue-200 bg-blue-50/60" },
  { value: "weekly",     label: "À la semaine", desc: "Forfait de 7 nuits, vendu par semaines entières", Icon: TbCalendar,     iconBg: "bg-[rgb(var(--k-green-rgb)/0.1)]", iconColor: "text-[var(--k-green)]", activeClass: "border-[rgb(var(--k-green-rgb)/0.3)] bg-[rgb(var(--k-green-rgb)/0.05)]" },
  { value: "monthly",    label: "Au mois",      desc: "Forfait de 30 nuits, vendu par mois entiers",     Icon: TbCalendar,     iconBg: "bg-[rgb(var(--k-green-rgb)/0.1)]", iconColor: "text-[var(--k-green)]", activeClass: "border-[rgb(var(--k-green-rgb)/0.3)] bg-[rgb(var(--k-green-rgb)/0.05)]" },
];

const UNIT_LABEL  = { short_stay: "nuit", weekly: "semaine", monthly: "mois" };
const UNIT_NIGHTS = { short_stay: 1, weekly: 7, monthly: 30 };

const isPeriodType = (t) => t === "weekly" || t === "monthly";

// "long_stay" est l'ancienne valeur : les plans d'avant migration s'ouvrent en mensuel.
const normalizeStayType = (t) =>
  t === "long_stay" ? "monthly" : (["short_stay", "weekly", "monthly"].includes(t) ? t : "short_stay");

const MEAL_OPTIONS = [
  { value: "none", label: "Sans repas", emoji: null },
  { value: "breakfast", label: "Petit-déjeuner", emoji: "🍳" },
  { value: "half_board", label: "Demi-pension", emoji: "🍽️" },
  { value: "full_board", label: "Pension complète", emoji: "🍱" }
];

const DEFAULT_STATE = {
  name: "", description: "", mealPlan: "none",
  stayType: "short_stay",        // 🆕 PÉRIODES — plus de minNights saisi
  policyType: "free",
  penaltyMode: "percentage",     // 🆕 FRAIS FIXES
  penaltyPercentage: 30,
  penaltyFixedAmount: 20,        // 🆕 FRAIS FIXES
  hoursBeforeArrival: 24,
  baseOccupancy: 1, extraAdultFee: 0, extraChildFee: 0, childMaxAge: 12,
  taxEnabled: false, taxAmount: 2.05, taxIncluded: false,
};

const RatePlansDrawer = ({
  show, onHide, onCreate, onUpdate, hotelId, editingPlan,
  rooms = [], ratePrices = [],
}) => {
  const [form, setForm] = useState(DEFAULT_STATE);
  const [mounted, setMounted] = useState(false);
  const [submitError, setSubmitError] = useState(null);   // 🆕 refus du serveur
  const [saving, setSaving] = useState(false);
  const drawerRef = useRef(null);
  const [roomPriceMap, setRoomPriceMap] = useState({});  // roomId → ratePriceId (tous, actifs + inactifs)
  const [checkedRooms, setCheckedRooms] = useState(new Set()); // seulement les actifs
  const [loadingRooms, setLoadingRooms] = useState(new Set());
  const isEdit = Boolean(editingPlan);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  useEffect(() => {
    if (!show) { setMounted(false); return; }
    requestAnimationFrame(() => setMounted(true));
    setSubmitError(null);

    if (editingPlan) {
      setForm({
        name: editingPlan.name || "",
        description: editingPlan.description || "",
        mealPlan: editingPlan.mealPlan || "none",
        stayType: normalizeStayType(editingPlan.stayType),   // 🆕 PÉRIODES
        policyType: editingPlan.cancellationPolicy?.type || "free",
        penaltyMode: editingPlan.cancellationPolicy?.penaltyMode || "percentage",           // 🆕 FRAIS FIXES
        penaltyPercentage: editingPlan.cancellationPolicy?.penaltyPercentage || 30,
        penaltyFixedAmount: editingPlan.cancellationPolicy?.penaltyFixedAmount || 20,        // 🆕 FRAIS FIXES
        hoursBeforeArrival: editingPlan.cancellationPolicy?.hoursBeforeArrival || 24,
        baseOccupancy: editingPlan.extraGuestPolicy?.baseOccupancy || 1,
        extraAdultFee: editingPlan.extraGuestPolicy?.extraAdultFee || 0,
        extraChildFee: editingPlan.extraGuestPolicy?.extraChildFee || 0,
        childMaxAge: editingPlan.extraGuestPolicy?.childMaxAge || 12,
        taxEnabled:  editingPlan.taxConfig?.enabled  ?? false,
        taxAmount:   editingPlan.taxConfig?.amount   ?? 2.05,
        taxIncluded: editingPlan.taxConfig?.included ?? false,
      });

      const map = {};
      const checked = new Set();
      // 🆕 ASSOCIATION — les chambres associées viennent de editingPlan.roomIds
      (editingPlan.roomIds || []).forEach((id) => {
        checked.add(id?.toString?.() ?? id);
      });
      // ratePrices sert UNIQUEMENT au badge "prix configuré" : on ne garde que les vrais prix actifs
      ratePrices.forEach((rp) => {
        if (rp.active !== false && rp.price > 0) {
          const rid = rp.roomId?.toString?.() ?? rp.roomId;
          map[rid] = rp._id;
        }
      });
      setRoomPriceMap(map);
      setCheckedRooms(checked);
    } else {
      setForm(DEFAULT_STATE);
      setRoomPriceMap({});
      setCheckedRooms(new Set());
    }
  }, [editingPlan, show, ratePrices]);

  const handleRoomToggle = (roomId, nowChecked) => {
    setCheckedRooms((prev) => {
      const next = new Set(prev);
      nowChecked ? next.add(roomId) : next.delete(roomId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    setSubmitError(null);
    setSaving(true);

    const payload = {
      hotelId,
      name: form.name.trim(),
      description: form.description.trim(),
      mealPlan: form.mealPlan,
      // 🆕 PÉRIODES — minNights n'est plus envoyé : le serveur le déduit du
      // stayType (1 / 7 / 30). L'envoyer laisserait croire qu'il est modifiable.
      stayType: form.stayType,
      // 🆕 ASSOCIATION — l'association vit sur le plan. Plus aucun RatePrice créé ici.
      roomIds: [...checkedRooms],
      cancellationPolicy: {
        type: form.policyType,
        hoursBeforeArrival: form.policyType !== "non_refundable" ? Number(form.hoursBeforeArrival) : 0,
        // 🆕 FRAIS FIXES — seul le champ du mode actif est renseigné, l'autre reste à 0
        penaltyMode: form.policyType === "partial" ? form.penaltyMode : "percentage",
        penaltyPercentage: form.policyType === "partial" && form.penaltyMode === "percentage"
          ? Number(form.penaltyPercentage) : 0,
        penaltyFixedAmount: form.policyType === "partial" && form.penaltyMode === "fixed"
          ? Number(form.penaltyFixedAmount) : 0,
      },
      extraGuestPolicy: {
        baseOccupancy: Number(form.baseOccupancy),
        extraAdultFee: Number(form.extraAdultFee),
        extraChildFee: Number(form.extraChildFee),
        childMaxAge: Number(form.childMaxAge)
      },
      taxConfig: {
        enabled:  form.taxEnabled,
        amount:   Number(form.taxAmount),
        included: form.taxIncluded,
      },
    };

    try {
      // Le back gère tout : création/màj du plan + diff des associations.
      if (isEdit) {
        await onUpdate(editingPlan._id, payload);
      } else {
        await onCreate(payload);
      }
      setForm(DEFAULT_STATE);
      onHide();
    } catch (err) {
      // 🆕 Le serveur refuse certains cas (e-mail d'établissement manquant sur
      // un forfait, politique d'annulation incompatible). Sans affichage, le
      // bouton semblait ne rien faire.
      setSubmitError(err?.response?.data?.error || err?.message || "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setMounted(false);
    setTimeout(() => { setForm(DEFAULT_STATE); setSubmitError(null); onHide(); }, 200);
  };

  if (!show) return null;

  const checkedCount = checkedRooms.size;
  const isPeriod = isPeriodType(form.stayType);                    // 🆕 PÉRIODES
  const unitLabel = UNIT_LABEL[form.stayType];
  // 🆕 PÉRIODES — changer l'unité change le SENS des prix déjà saisis :
  // le serveur les désactive. L'hébergeur doit le savoir AVANT de valider.
  const unitChanged = isEdit && normalizeStayType(editingPlan.stayType) !== form.stayType;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className={`absolute inset-0 bg-black/25 backdrop-blur-[2px] transition-opacity duration-200 ${mounted ? "opacity-100" : "opacity-0"}`} onClick={handleClose} />

      <div ref={drawerRef} className={`relative w-full max-w-[420px] bg-white h-full flex flex-col shadow-[-20px_0_60px_rgba(0,0,0,0.12)] transition-transform duration-200 ease-out ${mounted ? "translate-x-0" : "translate-x-full"}`}>
        <div className="h-0.5 w-full bg-gradient-to-r from-[var(--k-green)] via-[#5abf3a] to-[rgb(var(--k-green-rgb)/0.3)]" />

        {/* HEADER */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <div className="w-5 h-5 rounded-md bg-[var(--k-green)] flex items-center justify-center">
                <TbTag className="text-white text-xs" />
              </div>
              <h2 className="text-sm font-bold text-gray-900 tracking-tight">{isEdit ? "Modifier le tarif" : "Nouveau tarif"}</h2>
            </div>
            <p className="text-[11px] text-gray-400 ml-7">{isEdit ? "Mettez à jour la configuration tarifaire" : "Configurez votre plan tarifaire"}</p>
          </div>
          <button onClick={handleClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-gray-400 hover:text-gray-600 mt-0.5">
            <TbX className="text-base" />
          </button>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <SectionTitle icon={TbTag} label="Informations" />

          <Field label="Nom du tarif *">
            <Input icon={TbTag} value={form.name} onChange={set("name")} placeholder="Ex : Tarif flexible, Non remboursable..." required />
          </Field>

          <Field label="Description" hint="Visible par les clients lors de la réservation">
            <textarea value={form.description} onChange={set("description")} placeholder="Décrivez les avantages de ce tarif..." rows={2}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-800 outline-none focus:border-[var(--k-green)] focus:bg-white transition-all resize-none placeholder:text-gray-300" />
          </Field>

          {/* 🆕 PÉRIODES — unité de vente */}
          <SectionTitle icon={TbCalendar} label="Unité de vente" />
          <Field label="Comment vendez-vous cette chambre ? *">
            <div className="flex flex-col gap-2">
              {STAY_TYPE_OPTIONS.map((opt) => (
                <StayTypeCard key={opt.value} opt={opt} selected={form.stayType === opt.value}
                  onClick={() => setForm((f) => {
                    if (f.stayType === opt.value) return f;
                    // 🆕 PÉRIODES — un forfait impose l'annulation partielle
                    // (règle serveur). Le délai bascule sur un défaut cohérent
                    // avec l'unité : 30 jours au mois, 7 jours à la semaine.
                    if (opt.value === "monthly") {
                      return { ...f, stayType: "monthly", policyType: "partial", hoursBeforeArrival: 720 };
                    }
                    if (opt.value === "weekly") {
                      return { ...f, stayType: "weekly", policyType: "partial", hoursBeforeArrival: 168 };
                    }
                    return { ...f, stayType: "short_stay", hoursBeforeArrival: 24 };
                  })} />
              ))}
            </div>
          </Field>

          {/* 🆕 PÉRIODES — avertissement de changement d'unité, avant validation */}
          {unitChanged && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3">
              <span className="text-red-400 text-base mt-0.5 shrink-0">⚠️</span>
              <div>
                <p className="text-xs font-bold text-red-700 mb-1">Les prix de ce tarif seront désactivés</p>
                <p className="text-xs text-red-600 leading-relaxed">
                  Un prix saisi « par {UNIT_LABEL[normalizeStayType(editingPlan.stayType)]} » ne veut plus rien
                  dire « par {unitLabel} ». Vous devrez ressaisir vos montants dans la grille tarifaire.
                </p>
              </div>
            </div>
          )}

          {isPeriod && (
            <div className="bg-[rgb(var(--k-green-rgb)/0.05)] border border-[rgb(var(--k-green-rgb)/0.15)] rounded-xl px-4 py-3 flex flex-col gap-1.5">
              <p className="text-[11px] font-bold text-[var(--k-green)] uppercase tracking-widest">Comment ça marche</p>
              <p className="text-xs text-gray-600">
                Le client choisit une date d'arrivée et un nombre de <span className="font-semibold text-gray-800">{unitLabel === "mois" ? "mois" : `${unitLabel}s`}</span>.
                La date de départ est calculée : {UNIT_NIGHTS[form.stayType]} nuits par {unitLabel}.
              </p>
              <p className="text-xs text-gray-600">
                Dans la grille tarifaire, saisissez le <span className="font-semibold text-gray-800">prix d'{unitLabel === "mois" ? "un mois" : "une semaine"} entier{unitLabel === "mois" ? "" : "e"}</span>, et non un prix par nuit.
              </p>
              <p className="text-xs text-gray-600">
                Ce tarif n'apparaît jamais dans une recherche à la nuit, et son prix n'est pas utilisé pour le « à partir de » de votre établissement.
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5">
                💡 La réservation est bloquée dès la demande, puis confirmée par vous une fois le règlement reçu. Votre adresse e-mail d'établissement doit être renseignée.
              </p>
            </div>
          )}

          <SectionTitle icon={TbToolsKitchen2} label="Restauration" />
          <Field label="Repas inclus">
            <div className="grid grid-cols-2 gap-2">
              {MEAL_OPTIONS.map((opt) => (
                <MealChip key={opt.value} opt={opt} selected={form.mealPlan === opt.value}
                  onClick={() => setForm((f) => ({ ...f, mealPlan: opt.value }))} />
              ))}
            </div>
          </Field>

          <SectionTitle icon={TbShield} label="Annulation" />

          {/* 🆕 PÉRIODES — sur un forfait, seule "Annulation partielle" est autorisée :
              pas de gratuite (trop risqué pour l'hébergeur sur un engagement long),
              pas de non-remboursable (trop brutal pour le client). Les 2 autres
              cartes sont masquées, et le serveur refuse de toute façon. */}
          {isPeriod && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-start gap-3">
              <TbShieldHalf className="text-amber-500 text-base mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700 leading-relaxed">
                Les forfaits imposent une annulation partielle : le client bénéficie d'une période gratuite, puis des frais s'appliquent après le délai.
              </p>
            </div>
          )}

          <Field label="Politique d'annulation *">
            <div className="flex flex-col gap-2">
              {(isPeriod ? POLICY_OPTIONS.filter((opt) => opt.value === "partial") : POLICY_OPTIONS).map((opt) => (
                <PolicyCard key={opt.value} opt={opt} selected={form.policyType === opt.value}
                  onClick={() => setForm((f) => ({ ...f, policyType: opt.value }))} />
              ))}
            </div>
          </Field>

          {form.policyType !== "non_refundable" && (
            // 🆕 PÉRIODES — le délai se saisit en jours sur un forfait (comme
            // Airbnb « 30 jours avant l'arrivée »), converti en heures en
            // interne pour ne rien changer au calcul backend.
            <Field label="Délai d'annulation gratuite" hint="Annulation sans frais si effectuée avant ce délai">
              <NumInput
                icon={TbClock}
                min={0}
                value={isPeriod ? Math.round(form.hoursBeforeArrival / 24) : form.hoursBeforeArrival}
                onChange={(e) => {
                  const val = Number(e.target.value) || 0;
                  setForm((f) => ({ ...f, hoursBeforeArrival: isPeriod ? val * 24 : val }));
                }}
                suffix={isPeriod ? "jours" : "heures"}
              />
            </Field>
          )}

          {/* 🆕 FRAIS FIXES — toggle % vs € pour l'annulation partielle */}
          {form.policyType === "partial" && (
            <>
              <Field label="Type de frais" hint="Comment les frais d'annulation hors délai sont calculés">
                <div className="grid grid-cols-2 gap-2">
                  <PenaltyModeCard
                    label="Pourcentage"
                    desc="% du montant total"
                    selected={form.penaltyMode === "percentage"}
                    onClick={() => setForm((f) => ({ ...f, penaltyMode: "percentage" }))}
                  />
                  <PenaltyModeCard
                    label="Montant fixe"
                    desc="Somme en €"
                    selected={form.penaltyMode === "fixed"}
                    onClick={() => setForm((f) => ({ ...f, penaltyMode: "fixed" }))}
                  />
                </div>
              </Field>

              {form.penaltyMode === "percentage" ? (
                <Field label="Frais d'annulation" hint="Pourcentage prélevé du montant total si annulation hors délai">
                  <NumInput icon={TbPercentage} min={1} max={100} value={form.penaltyPercentage} onChange={set("penaltyPercentage")} suffix="%" />
                </Field>
              ) : (
                <Field label="Frais d'annulation" hint="Montant fixe retenu si annulation hors délai">
                  <NumInput icon={TbCurrencyEuro} min={1} step={0.01} value={form.penaltyFixedAmount} onChange={set("penaltyFixedAmount")} suffix="€" />
                </Field>
              )}
            </>
          )}

          <SectionTitle icon={TbUsers} label="Occupants" />
          <Field label="Occupation de base incluse" hint="Nombre de personnes incluses dans le tarif de base">
            <NumInput icon={TbHome} min={1} max={10} value={form.baseOccupancy} onChange={set("baseOccupancy")} suffix="pers." />
          </Field>

          {/* 🆕 PÉRIODES — les suppléments ne s'appliquent pas sur un forfait :
              le prix couvre le logement, l'occupation est bornée par la capacité
              de la chambre. Le dire évite une grille remplie pour rien. */}
          {isPeriod ? (
            <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
              <p className="text-xs text-gray-500 leading-relaxed">
                Sur un forfait, les suppléments par personne ne s'appliquent pas : le prix couvre
                le logement entier, dans la limite de la capacité de la chambre.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Supplément adulte" hint={`Par nuit, au-delà de ${form.baseOccupancy} pers.`}>
                  <NumInput icon={TbUserPlus} min={0} value={form.extraAdultFee} onChange={set("extraAdultFee")} suffix="€" />
                </Field>
                <Field label="Supplément enfant" hint="Par enfant, par nuit">
                  <NumInput icon={TbMoodKid} min={0} value={form.extraChildFee} onChange={set("extraChildFee")} suffix="€" />
                </Field>
              </div>

              <Field label="Âge maximum enfant" hint="En dessous de cet âge → tarif enfant">
                <NumInput icon={TbMoodKid} min={0} max={17} value={form.childMaxAge} onChange={set("childMaxAge")} suffix="ans" />
              </Field>

              {(Number(form.extraAdultFee) > 0 || Number(form.extraChildFee) > 0) && (
                <div className="bg-[rgb(var(--k-green-rgb)/0.05)] border border-[rgb(var(--k-green-rgb)/0.15)] rounded-xl px-4 py-3 flex flex-col gap-1.5">
                  <p className="text-[11px] font-bold text-[var(--k-green)] uppercase tracking-widest">Récapitulatif</p>
                  <p className="text-xs text-gray-600">Tarif de base pour <span className="font-semibold text-gray-800">{form.baseOccupancy} personne{form.baseOccupancy > 1 ? "s" : ""}</span></p>
                  {Number(form.extraAdultFee) > 0 && <p className="text-xs text-gray-600">+ <span className="font-semibold text-gray-800">{form.extraAdultFee} €</span>/nuit par adulte supplémentaire</p>}
                  {Number(form.extraChildFee) > 0 && <p className="text-xs text-gray-600">+ <span className="font-semibold text-gray-800">{form.extraChildFee} €</span>/nuit par enfant (moins de {form.childMaxAge} ans)</p>}
                </div>
              )}
            </>
          )}

          <SectionTitle icon={TbReceipt} label="Taxe de séjour" />
          <Field hint="Taxe légale comorienne — 1 personne · 1 nuit">
            <div onClick={() => setForm(f => ({ ...f, taxEnabled: !f.taxEnabled }))}
              className={`flex items-center justify-between px-4 py-3 rounded-xl border cursor-pointer transition-all duration-150 select-none ${form.taxEnabled ? "border-[var(--k-green)] bg-[rgb(var(--k-green-rgb)/0.05)]" : "border-gray-100 bg-gray-50 hover:border-gray-200"}`}>
              <div className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${form.taxEnabled ? "bg-[rgb(var(--k-green-rgb)/0.15)]" : "bg-gray-100"}`}>
                  {form.taxEnabled ? <TbReceipt className="text-[var(--k-green)] text-sm" /> : <TbReceiptOff className="text-gray-400 text-sm" />}
                </div>
                <div>
                  <p className={`text-sm font-semibold ${form.taxEnabled ? "text-gray-800" : "text-gray-500"}`}>{form.taxEnabled ? "Taxe activée" : "Taxe désactivée"}</p>
                  <p className="text-[10px] text-gray-400">Par personne · par nuit</p>
                </div>
              </div>
              <div className={`w-9 h-5 rounded-full transition-all duration-200 relative ${form.taxEnabled ? "bg-[var(--k-green)]" : "bg-gray-200"}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200 ${form.taxEnabled ? "left-4" : "left-0.5"}`} />
              </div>
            </div>
          </Field>

          {form.taxEnabled && (
            <>
              <Field label="Montant de la taxe">
                <NumInput icon={TbCurrencyEuro} min={0} step={0.01} value={form.taxAmount} onChange={set("taxAmount")} suffix="€ / pers. / nuit" />
              </Field>
              <Field label="Mode d'affichage">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: false, label: "En supplément", desc: "Affichée séparément" },
                    { value: true,  label: "Incluse", desc: "Dans le prix total" },
                  ].map(opt => (
                    <div key={String(opt.value)} onClick={() => setForm(f => ({ ...f, taxIncluded: opt.value }))}
                      className={`flex flex-col gap-0.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all duration-150 select-none ${form.taxIncluded === opt.value ? "border-[var(--k-green)] bg-[rgb(var(--k-green-rgb)/0.05)]" : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"}`}>
                      <div className="flex items-center justify-between">
                        <p className={`text-sm font-semibold ${form.taxIncluded === opt.value ? "text-gray-800" : "text-gray-500"}`}>{opt.label}</p>
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${form.taxIncluded === opt.value ? "border-[var(--k-green)] bg-[var(--k-green)]" : "border-gray-300"}`}>
                          {form.taxIncluded === opt.value && <TbCheck className="text-white text-[10px] stroke-[3]" />}
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-400">{opt.desc}</p>
                    </div>
                  ))}
                </div>
              </Field>
              <div className="bg-[rgb(var(--k-green-rgb)/0.05)] border border-[rgb(var(--k-green-rgb)/0.15)] rounded-xl px-4 py-3 flex flex-col gap-1">
                <p className="text-[11px] font-bold text-[var(--k-green)] uppercase tracking-widest">Aperçu</p>
                <p className="text-xs text-gray-600">Taxe de <span className="font-semibold text-gray-800">{form.taxAmount} €</span> / personne / nuit</p>
                <p className="text-xs text-gray-500">{form.taxIncluded ? "→ Le prix affiché inclut déjà la taxe" : "→ La taxe s'ajoutera au prix de la chambre"}</p>
              </div>
            </>
          )}

          {/* ── Chambres associées ── */}
          {rooms.length > 0 && (
            <>
              <SectionTitle icon={TbDoor} label="Chambres associées" />
              <Field label={`Chambres (${checkedCount}/${rooms.length})`} hint="Les associations seront appliquées à la validation du formulaire">
                <div className="flex flex-col gap-2">
                  {rooms.map((room) => {
                    const rid = room._id?.toString?.() ?? room._id;
                    const isChecked = checkedRooms.has(rid);
                    // ✅ hasPrice = coché ET dans roomPriceMap (prix existant actif ou inactif)
                    const hasPrice = isChecked && Boolean(roomPriceMap[rid]);
                    return (
                      <RoomChip key={rid} room={room} checked={isChecked} hasPrice={hasPrice}
                        loading={loadingRooms.has(rid)} onChange={handleRoomToggle} />
                    );
                  })}
                </div>
              </Field>

              {/* ✅ Bandeau amber : chambres cochées sans aucun RatePrice (même inactif) */}
              {isEdit && checkedCount > 0 && [...checkedRooms].some((rid) => !roomPriceMap[rid]) && (
                <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-start gap-3">
                  <span className="text-amber-400 text-base mt-0.5">⚠️</span>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Certaines chambres sont associées mais n'ont pas encore de prix défini. Configurez-les depuis la grille tarifaire.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t border-gray-100 bg-white">
          {/* 🆕 Refus du serveur affiché au-dessus du bouton */}
          {submitError && (
            <div className="mb-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-2.5">
              <span className="text-red-400 text-base mt-0.5 shrink-0">⚠️</span>
              <p className="text-xs text-red-700 leading-relaxed">{submitError}</p>
            </div>
          )}
          <button onClick={handleSubmit} disabled={!form.name.trim() || saving}
            className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 ${(!form.name.trim() || saving) ? "bg-gray-100 text-gray-300 cursor-not-allowed" : "bg-[var(--k-green)] text-white hover:bg-[var(--k-green-hover)] active:scale-[0.98] shadow-sm shadow-[rgb(var(--k-green-rgb)/0.2)]"}`}>
            {saving ? "Enregistrement..." : isEdit ? "Mettre à jour le tarif" : "Créer le tarif"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RatePlansDrawer;